import React, { useState } from 'react';
import { Box, Button, IconButton, SwipeableDrawer, Typography, } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Edit as EditIcon } from '@mui/icons-material';
import { CurrencyInput } from 'components';
import { getNextMonthName } from '../lib/getNextMonthName';
import { useTranslation } from 'hooks';
import { grey } from '@mui/material/colors';
import { formatCurrency } from '../../../../screens/finance/lib/formatCurrency';

const moneyInputProps = {
  autoComplete: 'off',
  inputMode: 'numeric' as const,
  pattern: undefined,
};

const validationSchema = Yup.object({
  profit: Yup.string().max(10, 'Не более 10 символов'),
  revenue: Yup.string().max(10, 'Не более 10 символов'),
});

interface FormValues {
  profit: string;
  revenue: string;
}

interface PlansDrawerProps {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  branch: 'urfo' | 'msk';
  initialValues: FormValues;
  onSubmit: (values: FormValues) => void | Promise<void>;
}

export const PlansDrawer = ({
  open,
  onClose,
  onOpen,
  branch,
  initialValues,
  onSubmit,
}: PlansDrawerProps) => {
  const { t } = useTranslation();
  const isMsk = branch === 'msk';
  const [isEditing, setIsEditing] = useState(false);

  const hasValues = isMsk
    ? Boolean(initialValues.profit)
    : Boolean(initialValues.profit || initialValues.revenue);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      disableSwipeToOpen
      sx={{ zIndex: 1300 }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          maxWidth: '100%',
          width: '100%',
        },
      }}
    >
      <Box sx={{ padding: '24px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '20px',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              backgroundColor: '#e0e0e0',
              borderRadius: 2,
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
            }}
          >
            Планы на {getNextMonthName()}
          </Typography>

          {hasValues && !isEditing && (
            <IconButton
              onClick={() => setIsEditing(true)}
              sx={{
                backgroundColor: grey[200],
                '&:hover': {
                  backgroundColor: grey[300],
                },
              }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>

        {hasValues && !isEditing ? (
          <Box sx={{ marginBottom: '20px' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: grey[100],
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '12px',
              }}
            >
              <Typography sx={{ fontWeight: 500, fontSize: 18 }}>
                {t('VP')}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                {formatCurrency(+initialValues.profit)}
              </Typography>
            </Box>

            {!isMsk && initialValues.revenue && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: grey[100],
                  borderRadius: '12px',
                  padding: '12px 16px',
                }}
              >
                <Typography sx={{ fontWeight: 500, fontSize: 18 }}>
                  {t('revenue')}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                  {formatCurrency(+initialValues.revenue)}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Formik<FormValues>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              onSubmit(values);
              setIsEditing(false);
            }}
            enableReinitialize
          >
            {({
              values,
              handleChange,
              handleBlur,
              errors,
              touched,
              isSubmitting,
            }) => (
              <Form>
                <Box sx={{ marginBottom: '20px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px',
                    }}
                  >
                    <Typography sx={{ fontWeight: 500 }}>{t('VP')}</Typography>
                    <CurrencyInput
                      id="profit"
                      name="profit"
                      value={values.profit}
                      onChangeEvent={handleChange}
                      onBlur={handleBlur}
                      showCurrency={false}
                      acceptRegex={/\d+/g}
                      error={touched.profit && Boolean(errors.profit)}
                      helperText={touched.profit && errors.profit}
                      variant="outlined"
                      placeholder={t('enterVP')}
                      inputProps={moneyInputProps}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                        },
                      }}
                    />
                  </Box>

                  {!isMsk && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography sx={{ fontWeight: 500 }}>
                        {t('revenue')}
                      </Typography>
                      <CurrencyInput
                        id="revenue"
                        name="revenue"
                        value={values.revenue}
                        onChangeEvent={handleChange}
                        onBlur={handleBlur}
                        showCurrency={false}
                        acceptRegex={/\d+/g}
                        error={touched.revenue && Boolean(errors.revenue)}
                        helperText={touched.revenue && errors.revenue}
                        variant="outlined"
                        placeholder={t('enterRevenue')}
                        inputProps={moneyInputProps}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  type="submit"
                  disabled={isSubmitting}
                  sx={{
                    borderRadius: '16px',
                    padding: '14px',
                    fontSize: '16px',
                    fontWeight: 600,
                    backgroundColor: '#FFB300',
                  }}
                >
                  {t('savePlans')}
                </Button>
              </Form>
            )}
          </Formik>
        )}
      </Box>
    </SwipeableDrawer>
  );
};
